<template>
  <v-card class="pa-3">
    <!-- close -->
    <v-card-title>
      <v-row class="justify-end">
        <a
          href="javascript: void(0)"
          role="button"
          aria-label="Close"
          @click="(v$.$anyDirty && (cancelDialog = true)) || closeAddChild()"
        >
          <v-icon :icon="mdiClose" />
        </a>
      </v-row>
    </v-card-title>
    <OnexAlert
      class="mb-4"
      :type="Notifications.error"
      dismissible="false"
      v-if="addChildError"
      @click:close="onCloseAlert"
    >
      {{ $t(addChildErrorMessage) }}
    </OnexAlert>

    <v-card-text>
      <v-row>
        <v-form
          id="policy_overview_form"
          ref="form"
          v-model="validForm"
          @submit.prevent.stop="submit"
        >
          <!-- Add a Child to Your Child Rider -->
          <!-- Provide child's information below... -->
          <v-col
            cols="12"
            md="6"
            offset-md="3"
            lg="6"
            offset-lg="3"
            xl="6"
            offset-xl="3"
            class="text-center"
          >
            <h2 aria-live="assertive" class="font-weight-regular">
              {{ $t('L_add_child_dialog_title') }}
            </h2>
            <h4 class="mt-3 font-weight-regular">
              {{ $t('L_add_child_dialog_text') }}
            </h4>
          </v-col>
          <!-- Add Child Form -->
          <v-col
            cols="12"
            md="6"
            offset-md="3"
            lg="6"
            offset-lg="3"
            xl="6"
            offset-xl="3"
            class="mb-12"
          >
            <v-row>
              <v-col cols="12" class="mt-4 pt-3">
                <OnexFirstName
                  v-model="firstName"
                  data-comp-id="first_name"
                  :label="$t('L_firstname')"
                  :auto-capitalize="true"
                  required
                />
              </v-col>
              <v-col cols="12">
                <OnexMiddleInitial
                  data-comp-id="middle_name"
                  v-model="middleName"
                  :label="$t('L_middle_name')"
                  :auto-capitalize="true"
                />
              </v-col>
              <v-col cols="12">
                <OnexLastName
                  data-comp-id="last_name"
                  v-model="lastName"
                  :label="$t('L_last_name')"
                  :auto-capitalize="true"
                  required
                />
              </v-col>
              <v-col cols="12">
                <OnexDatePicker
                  data-comp-id="birth_date"
                  type="dob"
                  class="mt-0"
                  :label="$t('L_birth_date')"
                  :date-format="dobFormatLanguage"
                  :max="maxDateOfBirth"
                  :min="minDateOfBirth"
                  :out-of-range-message="outOfRangeMessageText"
                  required
                  sensitive
                  v-model="birthDate"
                  :key="$i18n.locale"
                />
              </v-col>
              <v-col cols="12">
                <OnexSsn
                  data-comp-id="ssn"
                  :format="getCountry !== 'ca' ? SSNFormat.SSN : SSNFormat.SIN"
                  allow-sin-business
                  allow-sin-itn
                  :info="ssnTooltipContent"
                  v-model="ssn"
                  :label="getSSNLabel"
                />
              </v-col>

              <v-col cols="12" class="py-0">
                <OnexRadioGroup
                  id="gender"
                  v-model="gender"
                  :mandatory="true"
                  :label="$t('L_relationship_to_the_primary_insured')"
                  required
                  data-comp-id="gender"
                >
                  <OnexRadio
                    class="mb-4"
                    v-for="(option, index) in genders"
                    :id="'radio_input_gender_' + index"
                    :key="option.label"
                    :label="option.label"
                    :value="option.value"
                  >
                    <template #label>
                      <span :class="$vuetify.display.smAndUp ? 'font-size--16' : 'font-size--14'">
                        {{ $t(`${option.label}`) }}
                      </span>
                    </template>
                  </OnexRadio>
                </OnexRadioGroup>
              </v-col>

              <v-col cols="12" class="py-0">
                <OnexRadioGroup
                  id="resideWith"
                  data-comp-id="resideWith"
                  v-model="resideWith"
                  :mandatory="true"
                  :label="$t('L_does_the_child_reside_with')"
                  required
                >
                  <OnexRadio
                    class="mb-4"
                    v-for="(option, index) in resideWithOptions"
                    :id="'radio_input_reside_' + index"
                    :key="option.label"
                    :label="option.label"
                    :value="option.value"
                  >
                    <template #label>
                      <span :class="$vuetify.display.smAndUp ? 'font-size--16' : 'font-size--14'">
                        {{ $t(`${option.label}`) }}
                      </span>
                    </template>
                  </OnexRadio>
                </OnexRadioGroup>
              </v-col>

              <v-col cols="12" class="py-0">
                <v-row class="d-flex align-center justify-center py-4">
                  <v-col cols="12" sm="6" md="6" class="pr-4">
                    <OnexButton
                      id="add_child_save"
                      block
                      color="primary"
                      size="large"
                      :disabled="invalidFields"
                      @click.prevent="submit"
                      type="submit"
                      form="policy_overview_form"
                    >
                      {{ $t('L_add_a_child') }}
                    </OnexButton>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" class="pr-4">
                    <OnexButton
                      id="add_child_cancel_trigger"
                      block
                      color="primary"
                      variant="outlined"
                      size="large"
                      aria-label="Close"
                      @click.prevent="(v$.$anyDirty && (cancelDialog = true)) || closeAddChild()"
                    >
                      {{ $t('L_cancel') }}
                    </OnexButton>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-form>
      </v-row>
    </v-card-text>

    <!-- cancel dialog -->
    <v-dialog v-model="cancelDialog" width="auto">
      <v-card max-width="380" class="pa-3">
        <!-- close x -->
        <v-card-title>
          <v-row align-content="center" class="text-h5 font-weight-medium text-warning">
            <v-icon class="warning-icon text-warning mr-3" :icon="mdiAlert" />
            <span class="text-h4" aria-live="assertive">{{ $t('L_are_you_sure_title') }}</span>
            <v-spacer />
            <a
              class="add_child_cancel_close"
              role="button"
              href="javascript: void(0)"
              @click="cancelDialog = false"
            >
              <v-icon :icon="mdiClose" />
            </a>
          </v-row>
        </v-card-title>
        <!-- Are you Sure? -->
        <v-card-text>
          <span>{{ $t('L_are_you_sure_text') }}</span>
          <br />
          <span>{{ $t('L_data_will_be_lost') }}</span>
        </v-card-text>
        <!-- Action buttons -->
        <v-card-actions>
          <v-row justify="end">
            <OnexButton
              class="text-blue mr-2"
              size="small"
              variant="flat"
              @click="((cancelDialog = false), closeAddChild())"
            >
              {{ $t('L_yes_button') }}
            </OnexButton>
            <OnexButton
              class="text-blue add_child_cancel_close"
              size="small"
              variant="flat"
              @click="cancelDialog = false"
            >
              {{ $t('L_no_button') }}
            </OnexButton>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';

import { formatFromQuebec, formatFromUS } from './dateValidation';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mapActions, mapState } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { STEP } from '@/modules/childrider/store/childriderStore.model';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mdiAlert, mdiClose } from '@mdi/js';
import {
  DateFormats,
  OnexButton,
  OnexRadio,
  OnexRadioGroup,
  UILocale,
  OnexDatePicker,
  OnexLastName,
  OnexFirstName,
  OnexMiddleInitial,
  OnexSsn,
  OnexAlert,
  Notifications,
  SSNFormat,
} from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'AddChild',
  components: {
    OnexButton,
    OnexRadioGroup,
    OnexRadio,
    OnexDatePicker,
    OnexLastName,
    OnexFirstName,
    OnexMiddleInitial,
    OnexSsn,
    OnexAlert,
    Notifications,
  },
  data: () => ({
    firstName: '',
    middleName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    resideWith: 'Y',
    validForm: false,
    ssn: '',
    addChildError: false,
    addChildErrorCode: undefined as number | undefined,
    showTooltip: false,
    cancelDialog: false,
    v$: useVuelidate(),
    mdiClose,
    mdiAlert,
  }),
  validations() {
    return {
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        required,
        minLength: minLength(2),
      },
      birthDate: {
        required,
      },
      gender: {
        required,
      },
      ssn: {
        required,
      },
      resideWith: {
        required,
      },
    };
  },
  computed: {
    SSNFormat() {
      return SSNFormat;
    },
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
      issueDateForCurrentPolicy: 'issueDateForCurrentPolicy',
      step: 'step',
    }),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    getCountry() {
      return (this.currentPolicy.isCanadaPolicy ?? false) ? 'ca' : 'us';
    },
    getSSNLabel() {
      return this.$i18n.locale !== 'en-US' ? this.$t('L_sin') : this.$t('L_ssn');
    },
    ssnTooltipContent() {
      return {
        title: '',
        text: this.$t('L_child_rider_ssn_tooltip'),
        type: 'INFO',
      };
    },
    invalidFields() {
      if (this.isUsPolicy) {
        return (
          this.firstName.length < 2 ||
          this.lastName.length < 2 ||
          !this.birthDate ||
          this.v$.birthDate?.$invalid ||
          this.gender.length === 0 ||
          this.resideWith.length === 0 ||
          !this.validForm ||
          this.v$.$anyError
        );
      } else if (this.isCanadianPolicy) {
        return (
          this.firstName.length < 2 ||
          this.lastName.length < 2 ||
          !this.birthDate ||
          this.v$.birthDate?.$invalid ||
          this.gender.length === 0 ||
          this.resideWith.length === 0 ||
          !this.validForm ||
          this.v$.$anyError
        );
      }
      return true;
    },
    outOfRangeMessageText() {
      const dobDate = new Date(this.birthDate);
      if (dobDate >= this.maxDateOfBirth) {
        return this.$t('L_child_too_young');
      } else if (dobDate <= this.dobOfMaxAge) {
        if (this.currentPolicy.nextGenPolicy) {
          return this.$t('L_child_too_old_next_gen');
        } else {
          return this.$t('L_child_too_old');
        }
      } else if (
        this.issueDateForCurrentPolicy &&
        new Date(this.issueDateForCurrentPolicy) >= dobDate
      ) {
        return this.$t('L_born_after_issued');
      }
    },
    dobOfMaxAge() {
      const maxAge = this.currentPolicy?.nextGenPolicy ? 17.5 : 24.5;
      const today = new Date();
      const dobOfMaxAge = new Date(today);
      dobOfMaxAge.setFullYear(today.getFullYear() - maxAge);
      return dobOfMaxAge;
    },
    minDateOfBirth() {
      if (
        !this.issueDateForCurrentPolicy ||
        new Date(this.issueDateForCurrentPolicy) < this.dobOfMaxAge
      ) {
        // return dob of max age when no issue data, or issue date is earlier.
        return this.dobOfMaxAge;
      }

      return new Date(this.issueDateForCurrentPolicy);
    },
    maxDateOfBirth() {
      const today = new Date();
      today.setDate(today.getDate() - 15);
      return today;
    },
    formattedBirthDate() {
      switch (true) {
        case this.isFrenchLocale:
          return formatFromQuebec(this.birthDate);
        default:
          return formatFromUS(this.birthDate);
      }
    },
    isUsPolicy() {
      return !!(this.currentPolicy?.isUSPolicy || this.currentPolicy?.isNewYorkPolicy);
    },
    isCanadianPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    isFrenchLocale() {
      if (this.locale?.length > 0) {
        if (this.locale.toLowerCase().indexOf('fr') > -1) {
          return true;
        }
        return false;
      }
      return false;
    },
    addChildErrorMessage() {
      switch (this.addChildErrorCode) {
        case 4001:
          return 'L_err_child_exists';
        case 4002:
          return 'L_err_child_birth_date_before_issue';
        case 4003:
          return 'L_err_child_less_than_15_days';
        case 4004:
          return 'L_err_child_older_than_24_5';
        case 4005:
          return 'L_err_child_max_9';
        default:
          break;
      }
      return 'L_add_child_error';
    },
    genders() {
      return [
        {
          label: this.$t('L_son'),
          value: 'M',
        },
        {
          label: this.$t('L_daughter'),
          value: 'F',
        },
      ];
    },
    resideWithOptions() {
      return [
        {
          label: this.$t('L_yes_button'),
          value: 'Y',
        },
        {
          label: this.$t('L_no_button'),
          value: 'N',
        },
      ];
    },
    dobFormatLanguage() {
      return this.$i18n.locale === UILocale.FR_CA.toString()
        ? DateFormats.DDMMYYYY
        : DateFormats.MMDDYYYY;
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      addChild: 'addChildRider',
    }),
    onCloseAlert() {
      this.addChildError = false;
      this.addChildErrorCode = undefined;
    },
    errCallback(err: { response?: { data?: { errorCode?: number } } } | null) {
      if (err?.response?.data?.errorCode) {
        this.addChildErrorCode = err.response.data.errorCode;
      }
      this.addChildError = true;
      this.$el.parentElement.scrollTop = 0;
    },
    resetForm() {
      this.firstName = '';
      this.lastName = '';
      this.middleName = '';
      this.gender = '';
      this.ssn = '';
      this.resideWith = 'Y';
      this.birthDate = '';
      this.v$.$reset();
    },
    closeAddChild(success: boolean | null = null) {
      /*
       * Emit a custom action when a user successfully submits an add a child request
       * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
       */
      this.resetForm();
      if (success) {
        const i = DynatraceClient.enterAction('add_child_successful_submission');
        DynatraceClient.leaveAction(i);
        this.$emit('close');
      } else {
        this.$emit('cancel');
      }
    },
    submit() {
      const child = {
        childFirstName: this.firstName,
        childMiddleName: this.middleName,
        childLastName: this.lastName,
        childGender: this.gender,
        childSSN: this.ssn,
        childBirthDate: this.formattedBirthDate,
        childRelationship: this.gender === 'F' ? 'D' : 'S',
        residentWithParent: this.resideWith,
      };
      this.addChild({
        policyNumber: this.currentPolicy.contractId,
        child,
      })
        .then(() => this.closeAddChild(true))
        .catch((e: unknown) => this.errCallback(e));
      // If a user came from the CRC's "Add Another Child" option,
      // we need to clear the step to ready so that it won't open
      // add a child modal to pop up when they come back to the Policy Details page.
      this.step = STEP.READY;
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
#relationship {
  width: 100%;

  button {
    width: 50%;
  }
}

.full-width {
  margin: 0 -16px 0 -16px;
}

.warning-icon {
  font-size: 32px;
}
</style>
